/**
 * API Utility Functions
 * 
 * This file provides utility functions for making API calls.
 * All API calls use the base URL from the configuration.
 */

import config from '../config';

/**
 * Get the full API URL by appending the endpoint to the base URL
 * @param {string} endpoint - API endpoint (e.g., '/users', '/auth/login')
 * @returns {string} Full API URL
 */
export const getApiUrl = (endpoint) => {
  const baseURL = config.api.baseURL.replace(/\/$/, ''); // Remove trailing slash
  const path = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;
  return `${baseURL}${path}`;
};

/**
 * Get authentication token from localStorage
 * @returns {string|null} Authentication token or null
 */
export const getAuthToken = () => {
  return localStorage.getItem(config.auth.tokenKey);
};

/**
 * Set authentication token in localStorage
 * @param {string} token - Authentication token
 */
export const setAuthToken = (token) => {
  localStorage.setItem(config.auth.tokenKey, token);
};

/**
 * Remove authentication token from localStorage
 */
export const removeAuthToken = () => {
  localStorage.removeItem(config.auth.tokenKey);
  localStorage.removeItem(config.auth.refreshTokenKey);
};

/**
 * Get default headers for API requests
 * @param {Object} additionalHeaders - Additional headers to include
 * @returns {Object} Headers object
 */
export const getHeaders = (additionalHeaders = {}) => {
  const headers = {
    ...config.api.headers,
    ...additionalHeaders,
  };

  // Add authentication token if available
  const token = getAuthToken();
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  return headers;
};

/**
 * Make an API request
 * @param {string} endpoint - API endpoint
 * @param {Object} options - Fetch options (method, body, headers, etc.)
 * @returns {Promise} Fetch promise
 */
export const apiRequest = async (endpoint, options = {}) => {
  const url = getApiUrl(endpoint);
  
  // If body is FormData, don't set Content-Type (browser will set it with boundary)
  // Otherwise, use default headers
  const headers = options.body instanceof FormData 
    ? (options.headers || {})
    : getHeaders(options.headers);
  
  // Add auth token if not already present
  if (!headers['Authorization']) {
    const token = getAuthToken();
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
  }

  const fetchOptions = {
    ...options,
    headers,
  };

  // Add timeout
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), config.api.timeout);
  fetchOptions.signal = controller.signal;

  try {
    const response = await fetch(url, fetchOptions);
    clearTimeout(timeoutId);

    // Handle non-JSON responses
    const contentType = response.headers.get('content-type');
    if (!contentType || !contentType.includes('application/json')) {
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return response;
    }

    const data = await response.json();

    // Handle error responses
    if (!response.ok) {
      // Create error object with response data
      const error = new Error(data.message || data.error || `HTTP error! status: ${response.status}`);
      error.response = data;
      error.status = response.status;
      
      // Extract rate limit information from headers
      if (response.status === 429) {
        const retryAfter = response.headers.get('Retry-After');
        const rateLimitLimit = response.headers.get('X-RateLimit-Limit');
        const rateLimitRemaining = response.headers.get('X-RateLimit-Remaining');
        const rateLimitReset = response.headers.get('X-RateLimit-Reset');
        
        // Priority: Retry-After header > error message parsing
        if (retryAfter) {
          error.retryAfter = parseInt(retryAfter, 10);
        } else {
          // Extract retry time from error message if header not available
          const retryMatch = error.message.match(/try again in (\d+) seconds?/i);
          if (retryMatch) {
            error.retryAfter = parseInt(retryMatch[1], 10);
          }
        }
        
        error.rateLimitLimit = rateLimitLimit ? parseInt(rateLimitLimit, 10) : null;
        error.rateLimitRemaining = rateLimitRemaining ? parseInt(rateLimitRemaining, 10) : null;
        error.rateLimitReset = rateLimitReset ? parseInt(rateLimitReset, 10) : null;
      }
      
      throw error;
    }

    return data;
  } catch (error) {
    clearTimeout(timeoutId);
    
    if (error.name === 'AbortError') {
      throw new Error('Request timeout');
    }
    
    // If error already has response data (from our error handling above), keep it
    // Otherwise, check if it's a network error
    if (!error.response && (error.message === 'Failed to fetch' || error.name === 'TypeError')) {
      // Network error - preserve the error but mark it
      const networkError = new Error('Network error. Please check your internet connection and try again.');
      networkError.originalError = error;
      networkError.isNetworkError = true;
      throw networkError;
    }
    
    if (config.features.debugMode) {
      console.error('API Request Error:', error);
    }
    
    throw error;
  }
};

/**
 * GET request
 * @param {string} endpoint - API endpoint
 * @param {Object} params - Query parameters
 * @returns {Promise} API response
 */
export const get = async (endpoint, params = {}) => {
  const queryString = new URLSearchParams(params).toString();
  const url = queryString ? `${endpoint}?${queryString}` : endpoint;
  return apiRequest(url, { method: 'GET' });
};

/**
 * POST request
 * @param {string} endpoint - API endpoint
 * @param {Object|FormData} data - Request body (can be FormData for file uploads)
 * @returns {Promise} API response
 */
export const post = async (endpoint, data = {}) => {
  const options = {
    method: 'POST',
  };

  // Handle FormData (for file uploads)
  if (data instanceof FormData) {
    options.body = data;
    // Don't set Content-Type header - let browser set it with boundary
    return apiRequest(endpoint, {
      ...options,
      headers: {},
    });
  }

  // Handle regular JSON data
  return apiRequest(endpoint, {
    ...options,
    body: JSON.stringify(data),
  });
};

/**
 * PUT request
 * @param {string} endpoint - API endpoint
 * @param {Object} data - Request body
 * @returns {Promise} API response
 */
export const put = async (endpoint, data = {}) => {
  return apiRequest(endpoint, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
};

/**
 * PATCH request
 * @param {string} endpoint - API endpoint
 * @param {Object} data - Request body
 * @returns {Promise} API response
 */
export const patch = async (endpoint, data = {}) => {
  return apiRequest(endpoint, {
    method: 'PATCH',
    body: JSON.stringify(data),
  });
};

/**
 * DELETE request
 * @param {string} endpoint - API endpoint
 * @returns {Promise} API response
 */
export const del = async (endpoint) => {
  return apiRequest(endpoint, { method: 'DELETE' });
};

/**
 * Upload file
 * @param {string} endpoint - API endpoint
 * @param {FormData} formData - FormData containing the file
 * @param {Function} onProgress - Optional progress callback
 * @returns {Promise} API response
 */
export const uploadFile = async (endpoint, formData, onProgress = null) => {
  const headers = getHeaders();
  // Remove Content-Type header to let browser set it with boundary
  delete headers['Content-Type'];

  const url = getApiUrl(endpoint);
  const token = getAuthToken();
  
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();

    if (onProgress) {
      xhr.upload.addEventListener('progress', (e) => {
        if (e.lengthComputable) {
          const percentComplete = (e.loaded / e.total) * 100;
          onProgress(percentComplete);
        }
      });
    }

    xhr.addEventListener('load', () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        try {
          const response = JSON.parse(xhr.responseText);
          resolve(response);
        } catch (e) {
          resolve(xhr.responseText);
        }
      } else {
        try {
          const error = JSON.parse(xhr.responseText);
          reject(new Error(error.message || `HTTP error! status: ${xhr.status}`));
        } catch (e) {
          reject(new Error(`HTTP error! status: ${xhr.status}`));
        }
      }
    });

    xhr.addEventListener('error', () => {
      reject(new Error('Network error'));
    });

    xhr.addEventListener('abort', () => {
      reject(new Error('Upload aborted'));
    });

    xhr.open('POST', url);
    Object.keys(headers).forEach((key) => {
      xhr.setRequestHeader(key, headers[key]);
    });
    xhr.send(formData);
  });
};

const apiExport = {
  get,
  post,
  put,
  patch,
  delete: del,
  uploadFile,
  getApiUrl,
  getAuthToken,
  setAuthToken,
  removeAuthToken,
};
export default apiExport;

